import React, { useMemo, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import {
  vendorFormSchema,
  type VendorFormData,
} from "../../../shared/schemas/outsourcing.schema";
import {
  lookupBusinessByTaxCode,
  generateCustomerCode,
} from "../../../shared/schemas/customer.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { focusFirstError } from "../../../lib/form/formUtils";
import { Save, Loader2, AlertTriangle, Search, Truck } from "lucide-react";
import { toast } from "sonner";

import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { NumericInput } from "@/components/shared/NumericInput";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { zodValidator } from "@tanstack/zod-form-adapter";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/\s+/g, " ")
    .trim();
}

interface VendorFormProps {
  defaultValues?: Partial<VendorFormData>;
  onSubmit: (data: VendorFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  isModal?: boolean;
  modalHeader?: React.ReactNode;
}

export type { VendorFormData };

export default function VendorForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
  isModal = false,
  modalHeader,
}: VendorFormProps) {
  const [isLookingUpTax, setIsLookingUpTax] = useState(false);

  const { data: existingVendors = [] } = useQuery({
    queryKey: ["vendors-all-check"],
    queryFn: () =>
      trpcClient.vendors.list.query({ limit: 50 }).then((res) => res.items),
  });

  const form = useForm({
    defaultValues: {
      code: defaultValues?.code ?? "",
      name: defaultValues?.name ?? "",
      shortName: defaultValues?.shortName ?? "",
      type: defaultValues?.type ?? "full_service",
      group: defaultValues?.group ?? "",
      address: defaultValues?.address ?? "",
      taxCode: defaultValues?.taxCode ?? "",
      representative: defaultValues?.representative ?? "",
      phone: defaultValues?.phone ?? "",
      website: defaultValues?.website ?? "",
      contactPerson: defaultValues?.contactPerson ?? "",
      mobile: defaultValues?.mobile ?? "",
      email: defaultValues?.email ?? "",
      deliveryAddress: defaultValues?.deliveryAddress ?? "",
      transitAddress: defaultValues?.transitAddress ?? "",
      bankName: defaultValues?.bankName ?? "",
      bankAccountNumber: defaultValues?.bankAccountNumber ?? "",
      bankAccountHolder: defaultValues?.bankAccountHolder ?? "",
      notes: defaultValues?.notes ?? "",
      tags: defaultValues?.tags ?? "",
      initialDebt: defaultValues?.initialDebt ?? undefined,
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: vendorFormSchema as any },
    onSubmit: async ({ value }) => onSubmit(value as VendorFormData),
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  // Exact duplicate detection that blocks submission
  const duplicateErrors = useMemo(() => {
    const currentName = normalize(form.state.values.name || "");
    const currentShortName = normalize(form.state.values.shortName || "");
    const currentTax = (form.state.values.taxCode || "").trim();
    const currentCode = (form.state.values.code || "").trim().toUpperCase();

    if (!currentName && !currentShortName && !currentTax && !currentCode)
      return [];

    return existingVendors
      .filter((c: any) => {
        if (defaultValues?.code && c.code === defaultValues.code) return false;

        const nameMatch =
          currentName && normalize(c.name || "") === currentName;
        const shortMatch =
          currentShortName && normalize(c.shortName || "") === currentShortName;
        const taxMatch = currentTax && (c.taxCode || "").trim() === currentTax;
        const codeMatch =
          currentCode && (c.code || "").trim().toUpperCase() === currentCode;

        return nameMatch || shortMatch || taxMatch || codeMatch;
      })
      .slice(0, 3);
  }, [
    form.state.values.name,
    form.state.values.shortName,
    form.state.values.taxCode,
    form.state.values.code,
    existingVendors,
    defaultValues?.code,
  ]);

  const hasDuplicateError = duplicateErrors.length > 0;

  // Helper — compact field
  const F = ({
    name,
    label,
    required,
    placeholder,
    type = "text",
    span = 1,
    onChangeHook,
    autoUpperCase,
  }: {
    name: keyof VendorFormData;
    label: string;
    required?: boolean;
    placeholder?: string;
    type?: string;
    span?: 1 | 2 | 3 | 4;
    onChangeHook?: (val: string, oldVal: string) => void;
    autoUpperCase?: boolean;
  }) => (
    <form.Field name={name}>
      {(field: any) => (
        <div
          className={cn(
            span === 2 && "col-span-2",
            span === 3 && "col-span-3",
            span === 4 && "col-span-4",
          )}
        >
          <TanstackFieldWrapper field={field} label={label} required={required}>
            <TanstackFormControl>
              <Input
                id={field.name}
                type={type}
                value={(field.state.value as string) ?? ""}
                placeholder={placeholder}
                className="h-9"
                onChange={(e) => {
                  const oldVal = (field.state.value as string) ?? "";
                  const newVal = autoUpperCase
                    ? e.target.value.toUpperCase()
                    : e.target.value;
                  field.handleChange(newVal);
                  onChangeHook?.(newVal, oldVal);
                }}
                onBlur={field.handleBlur}
              />
            </TanstackFormControl>
          </TanstackFieldWrapper>
        </div>
      )}
    </form.Field>
  );

  const SH = ({
    label,
    color = "text-indigo-700",
  }: {
    label: string;
    color?: string;
  }) => (
    <h3
      className={cn("text-sm font-bold uppercase tracking-wider pt-2", color)}
    >
      {label}
    </h3>
  );

  const taxCodeValue = useStore(form.store, (s: any) => s.values.taxCode);

  const handleLookupTax = async () => {
    let cleanTax = (taxCodeValue || "").trim();
    if (!cleanTax) {
      toast.warning("Vui lòng nhập Mã số thuế trước khi tra cứu.");
      return;
    }
    if (cleanTax.length === 13 && !cleanTax.includes("-")) {
      cleanTax = `${cleanTax.slice(0, 10)}-${cleanTax.slice(10)}`;
      form.setFieldValue("taxCode", cleanTax);
    }
    setIsLookingUpTax(true);
    try {
      const info = await lookupBusinessByTaxCode(cleanTax);
      if (info && info.name) {
        form.setFieldValue("name", info.name.toUpperCase());
        if (!form.getFieldValue("bankAccountHolder")) {
          form.setFieldValue("bankAccountHolder", info.name.toUpperCase());
        }
        if (info.address) {
          form.setFieldValue("address", info.address);
          if (!form.getFieldValue("deliveryAddress")) {
            form.setFieldValue("deliveryAddress", info.address);
          }
        }
        if (info.shortName) {
          const shortUpper = info.shortName.toUpperCase();
          form.setFieldValue("shortName", shortUpper);
          if (!form.getFieldValue("code") && !defaultValues?.code) {
            const suggested = generateCustomerCode(shortUpper);
            if (suggested) {
              form.setFieldValue("code", suggested);
            }
          }
        }
        if (info.status) {
          if (
            info.status.toLowerCase().includes("ngừng") ||
            info.status.toLowerCase().includes("đóng")
          ) {
            toast.warning(`Trạng thái MST: ${info.status}`);
          } else {
            toast.success(`Đã tự động lấy dữ liệu NCC: ${info.name}`);
          }
        } else {
          toast.success(`Đã tự động lấy dữ liệu NCC: ${info.name}`);
        }
      } else {
        toast.warning("Không tìm thấy thông tin công ty từ mã số thuế này.");
      }
    } catch {
      toast.error("Lỗi khi kết nối cổng tra cứu mã số thuế.");
    } finally {
      setIsLookingUpTax(false);
    }
  };

  const actionButtons = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onCancel}
        disabled={isSubmitting}
      >
        Huỷ bỏ
      </Button>
      <form.Subscribe selector={(s: any) => [s.canSubmit, s.isSubmitting]}>
        {([canSubmit, submitting]: any) => (
          <Button
            type="submit"
            size="sm"
            disabled={
              !canSubmit || submitting || isSubmitting || hasDuplicateError
            }
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting || isSubmitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Đang lưu...
              </>
            ) : (
              <>
                <Save className="mr-1.5 h-4 w-4" /> {submitLabel}
              </>
            )}
          </Button>
        )}
      </form.Subscribe>
    </div>
  );

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col space-y-4">
      {isModal && (
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-xs pb-2.5 pt-1 border-b border-slate-200 flex items-center justify-between gap-4 pr-6">
          <div className="min-w-0 flex-1">
            {modalHeader || (
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Truck className="w-5 h-5 text-sky-600 shrink-0" />
                <span>Thêm mới nhà cung cấp</span>
              </h3>
            )}
          </div>
          <div className="shrink-0">{actionButtons}</div>
        </div>
      )}

      <div className="space-y-5">
        {/* Duplicate Warnings - Blocking */}
        {duplicateErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-800">
                Lỗi: Thông tin trùng lặp với dữ liệu đã có. Vui lòng kiểm tra
                lại!
              </p>
              <ul className="mt-1 space-y-0.5 text-red-700">
                {duplicateErrors.map((c: any) => (
                  <li key={c.id}>
                    • <strong>{c.code}</strong> —{" "}
                    {c.shortName ? `[${c.shortName}] ` : ""}
                    {c.name} {c.taxCode ? `(MST: ${c.taxCode})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">
              Thông tin cơ bản & Liên hệ
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* HERO BAR: Tra cứu Mã số thuế 1-chạm (Đồng bộ với CustomerForm) */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
              <div className="flex-1 flex items-center gap-2">
                <div className="text-xs font-bold text-slate-700 whitespace-nowrap flex items-center gap-1">
                  <Search className="w-3.5 h-3.5 text-blue-600" /> Mã số thuế:
                </div>
                <form.Field
                  name="taxCode"
                  children={(field: any) => (
                    <Input
                      className="h-8 text-sm font-mono tracking-wider bg-white flex-1 max-w-xs"
                      placeholder="Nhập 10 hoặc 13 số..."
                      value={(field.state.value as string) || ""}
                      onChange={(e) => field.handleChange(e.target.value.trim())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleLookupTax();
                        }
                      }}
                      onBlur={field.handleBlur}
                    />
                  )}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleLookupTax}
                  disabled={isLookingUpTax || !taxCodeValue}
                  className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 shrink-0 flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {isLookingUpTax ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Đang tra...
                    </>
                  ) : (
                    <>
                      <Search className="w-3.5 h-3.5" /> Tra cứu MST 1-chạm
                    </>
                  )}
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 italic hidden sm:block">
                (Tự động điền Tên đầy đủ, Tên viết tắt, Mã NCC & Địa chỉ)
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <F
                name="name"
                label="Tên đầy đủ NCC / Cty"
                required
                placeholder="CÔNG TY TNHH..."
                span={2}
                autoUpperCase
                onChangeHook={(v) => {
                  requestAnimationFrame(() => {
                    const short = form.getFieldValue("shortName") as string;
                    const curCode = form.getFieldValue("code") as string;
                    if (v && short && !curCode && !defaultValues?.code) {
                      let newCode = normalize(short)
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .substring(0, 4)
                        .toUpperCase();
                      if (newCode) {
                        let counter = 1;
                        let baseCode = newCode;
                        while (
                          existingVendors.some((c: any) => c.code === newCode)
                        ) {
                          newCode = `${baseCode.substring(0, 4 - String(counter).length)}${counter}`;
                          counter++;
                        }
                        form.setFieldValue("code", newCode);
                      }
                    }
                  });
                }}
              />

              <F
                name="shortName"
                label="Tên viết tắt"
                required
                placeholder="ABC"
                autoUpperCase
                onChangeHook={(v) => {
                  requestAnimationFrame(() => {
                    const nameVal = form.getFieldValue("name") as string;
                    const curCode = form.getFieldValue("code") as string;
                    if (nameVal && v && !curCode && !defaultValues?.code) {
                      let newCode = normalize(v)
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .substring(0, 4)
                        .toUpperCase();
                      if (newCode) {
                        let counter = 1;
                        let baseCode = newCode;
                        while (
                          existingVendors.some((c: any) => c.code === newCode)
                        ) {
                          newCode = `${baseCode.substring(0, 4 - String(counter).length)}${counter}`;
                          counter++;
                        }
                        form.setFieldValue("code", newCode);
                      }
                    }
                  });
                }}
              />

              <F
                name="code"
                label="Mã NCC"
                required
                placeholder="Tự động hoặc tự nhập"
              />

              <F
                name="phone"
                label="Số điện thoại"
                placeholder="0281234567"
                onChangeHook={(v, oldVal) => {
                  const currentMobile = form.getFieldValue("mobile") as string;
                  if (!currentMobile || currentMobile === oldVal) {
                    form.setFieldValue("mobile", v);
                  }
                }}
              />
              <F name="email" label="Email" placeholder="info@company.vn" span={2} />
              <F name="website" label="Website" placeholder="company.vn" />
            </div>

            <div className="border-t border-slate-100" />

            {/* ── Địa chỉ ── */}
            <SH label="Khu vực & Địa chỉ" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <F
                name="address"
                label="Địa chỉ nhà máy/kho"
                required
                placeholder="Số nhà, đường, phường/xã..."
                span={2}
                onChangeHook={(v, oldVal) => {
                  const currentDelivery = form.getFieldValue(
                    "deliveryAddress",
                  ) as string;
                  if (!currentDelivery || currentDelivery === oldVal) {
                    form.setFieldValue("deliveryAddress", v);
                  }
                }}
              />

              <F
                name="deliveryAddress"
                label="Địa chỉ nhận hàng/Vp"
                placeholder="Nếu khác địa chỉ chính..."
                span={2}
              />

              <F
                name="transitAddress"
                label="Kho trung chuyển"
                placeholder="Kho ngoại quan, kho trung chuyển..."
                span={4}
              />
            </div>

            <div className="border-t border-slate-100" />

            {/* ── Đại diện & Kế toán ── */}
            <SH label="Đại diện & Thanh toán" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <form.Field name="type">
                {(field: any) => (
                  <div className="space-y-1">
                    <ComboboxConfigMaster
                      categoryCode="vendor_types"
                      value={field.state.value || ""}
                      onChange={field.handleChange}
                      label="Loại dịch vụ chính"
                      placeholder="Chọn dịch vụ..."
                    />
                  </div>
                )}
              </form.Field>
              <F
                name="representative"
                label="Người đại diện"
                placeholder="Nguyễn Văn A"
              />
              <F
                name="contactPerson"
                label="Người liên hệ"
                placeholder="Ms. B"
              />
              <F
                name="mobile"
                label="Di động liên hệ"
                placeholder="0901234567"
              />

              <F name="bankName" label="Ngân hàng" placeholder="Vietcombank" />
              <F
                name="bankAccountNumber"
                label="Số TK"
                placeholder="0123..."
                onChangeHook={(v, oldVal) => {
                  const currentHolder = form.getFieldValue(
                    "bankAccountHolder",
                  ) as string;
                  if (v && (!currentHolder || currentHolder === oldVal)) {
                    form.setFieldValue(
                      "bankAccountHolder",
                      form.getFieldValue("name") as string,
                    );
                  }
                }}
              />
              <F
                name="bankAccountHolder"
                label="Tên Chủ TK"
                placeholder="CTY TNHH..."
                span={2}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">
              Quản trị Giao dịch & Công nợ
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start">
              <form.Field name="group">
                {(field: any) => (
                  <div className="space-y-1">
                    <ComboboxConfigMaster
                      categoryCode="vendor_groups"
                      value={(field.state.value as string) || ""}
                      onChange={(v) => field.handleChange(v)}
                      label="Phân nhóm NCC"
                      placeholder="Chọn..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="tags">
                {(field: any) => (
                  <TanstackFieldWrapper
                    field={field}
                    label="Hạn mức công nợ (VNĐ)"
                  >
                    <TanstackFormControl>
                      <NumericInput
                        id={field.name}
                        value={
                          field.state.value !== "" && field.state.value != null
                            ? Number(field.state.value)
                            : undefined
                        }
                        onChange={(val: any) =>
                          field.handleChange(val != null ? String(val) : "")
                        }
                        placeholder="0"
                        className="h-9"
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
              <form.Field name="initialDebt">
                {(field: any) => (
                  <TanstackFieldWrapper field={field} label="Nợ đầu kỳ (VNĐ)">
                    <TanstackFormControl>
                      <NumericInput
                        id={field.name}
                        value={field.state.value as number | undefined}
                        onChange={(val) => field.handleChange(val)}
                        placeholder="0"
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>

            <div className="border-t border-slate-100" />

            {/* ── Ghi chú ── */}
            <form.Field name="notes">
              {(field: any) => (
                <div className="space-y-1 xl:col-span-4">
                  <Label className="text-xs">Ghi chú thêm</Label>
                  <Textarea
                    value={(field.state.value as string) ?? ""}
                    rows={2}
                    placeholder="Ghi chú về nhà cung cấp..."
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 z-20 bg-white/95 backdrop-blur-xs flex justify-end py-3 px-2 border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        {actionButtons}
      </div>
    </form>
  );
}
